import { useRef, useState } from "react";
import type { Exercise } from "../core/exercises";
import { setLabels } from "../core/templates";
import {
  adjustRest, cancelTimer, entryExercise, finishTimer, nextIntervalSet, pauseTimer, REST_STEP_SEC, resumeTimer, skipRest, stopChain,
  tapRep, timerState, type Workout,
} from "../core/workout";
import { useBack } from "./back";
import { ConfirmDialog } from "./ConfirmDialog";
import { HoldButton } from "./HoldButton";
import { SensorFix } from "./SensorFix";
import { formatDuration, t } from "./texts";

type Apply = (fn: (w: Workout, now: number) => Workout) => void;

/** Shown while a set timer runs, and during the rest of an interval (until the next set starts by itself). */
export const timerScreenShown = (w: Workout) => !!w.timer || !!w.rest?.chain;

/**
 * Full-screen set timer: huge time in a phase colour (yellow lead-in, green work, red last seconds, blue rest), for
 * `timed` sets a big tap area counting reps with −1 as undo; the stopwatch of a `distance` set counts up. During an
 * interval's rest it shows the rest with ±15 s, skip and "Intervall beenden".
 *
 * When the motion sensor counts (todo 8) the phone is in the pocket: the count is shown big but nothing counts a tap,
 * there is no pause, and "Fertig" must be held; the countdown ends the set by itself.
 */
export function TimerScreen(props: { workout: Workout; now: number; exercises: Exercise[]; apply: Apply }) {
  const { workout: w, now, apply } = props;
  const [confirmCancel, setConfirmCancel] = useState(false);
  // back = ✕ while a set runs; during an interval rest it leaves the screen to the App (app into the background)
  const cancelAction = useRef<() => void>(undefined);
  useBack(() => cancelAction.current?.(), !!w.timer);
  const tm = w.timer;
  const rest = !tm && w.rest?.chain ? w.rest : undefined;
  const at = tm ?? rest;
  const x = at && w.exercises.find((y) => y.key === at.exKey);
  if (!at || !x) return null;
  const e = entryExercise(props.exercises, x);
  const labels = setLabels(x.sets);
  const i = x.sets.findIndex((s) => s.key === at.setKey);
  const set = x.sets[i];
  const title = t.timer.setOf(e?.name ?? t.template.unknownExercise, labels[i] ?? "", x.sets.length);

  if (rest) {
    const remaining = Math.max(0, rest.endsAt - now);
    const next = nextIntervalSet(w, rest.exKey, rest.setKey);
    const ni = next ? x.sets.indexOf(next) : -1;
    return (
      <div className={"timer-screen " + (remaining <= 3000 ? "phase-ready" : "phase-rest")} role="dialog">
        <header className="timer-head">
          <button className="timer-ghost" onClick={() => apply(stopChain)}>{t.timer.stopInterval}</button>
        </header>
        <div className="timer-title">{title}</div>
        <div className="timer-label">{t.timer.rest}</div>
        <div className="timer-big">{formatDuration(Math.ceil(remaining / 1000))}</div>
        {next && <div className="timer-goal">{t.timer.next(t.timer.nextSet(labels[ni], formatDuration(next.durationSec ?? 0)))}</div>}
        <SensorFix workout={w} exercises={props.exercises} apply={apply} inTimer />
        <div className="tap-spacer" />
        <div className="timer-actions">
          <button className="timer-btn" onClick={() => apply((v, n) => adjustRest(v, -REST_STEP_SEC, n))}>−{REST_STEP_SEC}</button>
          <button className="timer-btn" onClick={() => apply((v, n) => adjustRest(v, REST_STEP_SEC, n))}>+{REST_STEP_SEC}</button>
          <button className="timer-btn strong" onClick={() => apply((v, n) => skipRest(v, props.exercises, n))}>{t.run.skip}</button>
        </div>
      </div>
    );
  }

  const timer = tm!;
  const st = timerState(timer, now);
  const paused = timer.pausedAt !== undefined;
  const stopwatch = timer.kind === "stopwatch";
  // a rep set by sensor (todo 17) counts up like the stopwatch, but is no stopwatch
  const countdown = timer.kind === "countdown";
  const phase = st.phase === "ready" ? "ready" : countdown && st.remaining <= 3000 ? "ending" : "work";
  const big = st.phase === "ready" ? String(Math.ceil(st.toStart / 1000))
    : countdown ? formatDuration(Math.ceil(st.remaining / 1000)) : formatDuration(Math.floor(st.elapsed / 1000));
  const label = paused ? t.timer.paused : st.phase === "ready" ? t.timer.ready : stopwatch ? t.timer.stopwatch : t.timer.work;
  const goal = timer.kind === "count" && set?.reps !== undefined ? t.timer.goalReps(set.reps)
    : set?.goal?.reps !== undefined ? t.timer.goalReps(set.goal.reps)
    : set?.goal?.durationSec !== undefined ? t.timer.goalTime(formatDuration(set.goal.durationSec)) : undefined;
  const cancel = () => (st.phase === "work" ? setConfirmCancel(true) : apply(cancelTimer));
  cancelAction.current = cancel;

  return (
    <div className={"timer-screen phase-" + (paused ? "paused" : phase)} role="dialog">
      <header className="timer-head">
        <button className="timer-ghost" onClick={cancel}>{t.timer.cancel}</button>
      </header>
      <div className="timer-title">{title}</div>
      <div className="timer-label">{label}</div>
      <div className="timer-big">{big}</div>
      {goal && <div className="timer-goal">{goal}</div>}
      {timer.sensor ? (
        <div className="sensor-count" role="status">
          <span className="tap-count">{timer.reps}</span>
          <span className="tap-hint">{t.sensor.counting}</span>
        </div>
      ) : timer.counting ? (
        <button className="tap-area" aria-label={t.timer.tapHint}
          onPointerDown={(ev) => { ev.preventDefault(); apply((v, n) => tapRep(v, 1, n)); }}>
          <span className="tap-count">{timer.reps}</span>
          <span className="tap-hint">{t.timer.tapHint}</span>
        </button>
      ) : <div className="tap-spacer" />}
      {timer.sensor ? (
        <div className="timer-actions">
          <HoldButton className="timer-btn strong" label={t.sensor.holdDone} onHeld={() => apply((v, n) => finishTimer(v, props.exercises, n))} />
        </div>
      ) : (
        <div className="timer-actions">
          {timer.counting && <button className="timer-btn" onClick={() => apply((v, n) => tapRep(v, -1, n))}>{t.timer.undo}</button>}
          <button className="timer-btn" onClick={() => apply(paused ? resumeTimer : pauseTimer)}>{paused ? t.timer.resume : t.timer.pause}</button>
          <button className="timer-btn strong" onClick={() => apply((v, n) => finishTimer(v, props.exercises, n))}>
            {stopwatch ? t.timer.stop : t.timer.done}
          </button>
        </div>
      )}
      {confirmCancel && (
        <ConfirmDialog text={t.timer.confirmCancel} confirm={t.timer.cancelYes} cancel={t.run.back} danger
          onCancel={() => setConfirmCancel(false)} onConfirm={() => { setConfirmCancel(false); apply(cancelTimer); }} />
      )}
    </div>
  );
}
